import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CompleteKundali, Language, ChartStyle } from '../types/kundali';
import {
  calculateIshtaKaal,
  getAvakahadaDetails,
  getGhatChakraDetails
} from '../utils/bhriguPatrikaCalculations';
import { NorthIndianSvgChart, SouthIndianSvgChart, EastIndianSvgChart } from './ChartViewer';
import { getNakshatraName, getZodiacName, getPlanetFullName } from '../utils/language';
import { formatDOB } from '../utils/dateUtils';
import {
  X,
  Globe,
  Compass,
  Clock,
  Layers,
  Award,
  AlertTriangle,
  Copy,
  Check,
  HelpCircle,
  FileText,
  LayoutGrid
} from 'lucide-react';

interface JatakaBirthBlueprintModalProps {
  isOpen: boolean;
  onClose: () => void;
  kundali: CompleteKundali;
  lang?: Language;
  chartStyle?: ChartStyle;
}

// Classical Jyotish Glossary for Interactive Tooltips
const CLASSICAL_GLOSSARY: Record<string, { hi: string; en: string }> = {
  ishtaKaal: {
    hi: 'स्थानीय सूर्योदय से जातक के जन्म समय तक का व्यतीत काल घटी-पल-विपल में (१ घटी = २४ मिनट)।',
    en: 'Elapsed time from local sunrise to native birth in Ghatis, Palas, and Vipalas (1 Ghati = 24 mins).'
  },
  dinmaan: {
    hi: 'सूर्योदय से सूर्यास्त तक दिन की कुल अवधि (६० घटी के अनुपात में)।',
    en: 'Total daylight duration from local sunrise to sunset in Ghatis.'
  },
  ratrimaan: {
    hi: 'सूर्यास्त से अगले सूर्योदय तक रात्रि की कुल अवधि।',
    en: 'Total nighttime duration from local sunset to next sunrise in Ghatis.'
  },
  sthanikSanskar: {
    hi: 'प्रामाणिक समय (IST: 82.5° E) व जन्मस्थान के देशांतर का समय अंतर (४ मिनट प्रति अंश)।',
    en: 'Longitude time difference between standard meridian (IST) and birthplace (4 mins per deg).'
  },
  sthanikSamaya: {
    hi: 'जन्म स्थान का वास्तविक स्थानीय मध्यम समय (LMT - Local Mean Time)।',
    en: 'Actual Local Mean Time (LMT) at native birthplace longitude.'
  },
  velantar: {
    hi: 'सूर्य की आभासी व वास्तविक मध्यम गति के अंतर का काल संस्कार (Equation of Time)।',
    en: 'Equation of time adjustment for Earth orbital eccentricity and axial tilt.'
  },
  samaptikKaal: {
    hi: 'वसंत विषुव बिंदु के सापेक्ष आकाशीय साम्पातिक नक्षत्र समय (Sidereal Time)।',
    en: 'Local Sidereal Time (LST) used for exact mathematical Ascendant calculation.'
  },
  bhayat: {
    hi: 'जन्म नक्षत्र में चंद्रमा द्वारा जन्म समय तक भोगी गई अवधि (व्यतीत घटी-पल)।',
    en: 'Elapsed portion of birth constellation traversed by Moon before birth in Ghatis.'
  },
  bhabhog: {
    hi: 'जन्म नक्षत्र का चंद्रमा द्वारा कुल भोग्य मान (आरंभ से अंत तक की कुल अवधि)।',
    en: 'Total duration of birth constellation traversed by Moon (~60 Ghatis).'
  },
  bhogyaDasha: {
    hi: 'भयात-भभोग के अनुपात से जन्म नक्षत्र स्वामी ग्रह की जन्म समय बची हुई दशा अवधि।',
    en: 'Remaining balance of the initial Vimshottari Mahadasha at the moment of birth.'
  },
  paya: {
    hi: 'जन्म समय चंद्रमा के लग्न से भाव स्थान अनुसार पाया (स्वर्ण=१,६,११, रजत=२,५,९, ताम्र=३,७,१०, लौह=४,८,१२)।',
    en: 'Vedic Paya based on Moon house from Lagna: Gold (1,6,11), Silver (2,5,9), Copper (3,7,10), Iron (4,8,12).'
  },
  varna: {
    hi: 'चंद्र राशि अनुसार स्वभाव व कर्म का आध्यात्मिक वर्गीकरण (ब्राह्मण, क्षत्रिय, वैश्य, शूद्र)।',
    en: 'Spiritual temperament and constitutional inclination governed by Moon sign.'
  },
  vashya: {
    hi: 'पारस्परिक आकर्षण, सामाजिक प्रभाव और वशीकरण सामर्थ्य।',
    en: 'Magnetic influence and degree of natural receptivity/submissiveness.'
  },
  yoni: {
    hi: 'जन्म नक्षत्र से निर्धारित शारीरिक मूल प्रकृति व सहज प्रवृत्तियों का पशु प्रतीक।',
    en: 'Instinctive animal archetype representing biological rhythm and core nature.'
  },
  gana: {
    hi: 'मानसिक गुण व प्रकृति: देव (सौम्य/सात्विक), मनुष्य (संतुलित), राक्षस (तेजस्वी/उग्र)।',
    en: 'Temperamental archetype: Deva (divine), Manushya (pragmatic human), Rakshasa (fiery).'
  },
  nadi: {
    hi: 'आनुवंशिक ऊर्जा प्रवाह व त्रिदोष संतुलन (आदि=वात, मध्य=पित्त, अन्त्य=कफ)।',
    en: 'Physiological channel and dosha balance: Adi (Vata), Madhya (Pitta), Antya (Kapha).'
  },
  hansak: {
    hi: 'पंचतत्व प्रधानता (अग्नि, पृथ्वी, वायु, जल) जो स्वभाव व निर्णय क्षमता को निर्धारित करती है।',
    en: 'Elemental dominance (Fire, Earth, Air, Water) indicating temperament.'
  },
  namakshar: {
    hi: 'जन्म नक्षत्र और चरण के अनुसार नामकरण का शुभ प्रथम अक्षर।',
    en: 'Auspicious phonetic syllable for naming based on Moon constellation quarter.'
  },
  ghatChakra: {
    hi: 'जन्म राशि अनुसार अति-संवेदनशील तिथियां, मास, वार व नक्षत्र जिनमें नवीन व शुभ कार्य वर्जित हैं।',
    en: 'Astrological avoidance matrix denoting vulnerable months, days, tithis, and stars.'
  }
};

/**
 * Interactive Classical Tooltip Badge
 */
const ClassicalTooltip: React.FC<{ termKey: string; lang: Language; children: React.ReactNode }> = ({
  termKey,
  lang,
  children
}) => {
  const [show, setShow] = useState(false);
  const info = CLASSICAL_GLOSSARY[termKey];
  const isHi = lang === 'hi' || lang === 'sa';

  if (!info) return <>{children}</>;

  return (
    <span
      className="relative inline-flex items-center gap-0.5 group cursor-help select-none"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={(e) => {
        e.stopPropagation();
        setShow((p) => !p);
      }}
    >
      {children}
      <HelpCircle className="w-2.5 h-2.5 text-[#B58A4A] opacity-60 hover:opacity-100 transition-opacity ml-0.5 shrink-0" />
      {show && (
        <span className="absolute z-[10000] bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-60 sm:w-68 p-2 rounded-xl bg-[#0B2625] text-[#FAF6ED] text-[10.5px] leading-relaxed shadow-xl border border-[#D4AF37]/50 font-sans pointer-events-none animate-fadeIn select-text text-left font-normal normal-case">
          <span className="block font-bold text-[#EAD8A4] mb-0.5 border-b border-white/10 pb-0.5">
            {isHi ? 'शास्त्रीय प्रमाण' : 'Classical Jyotish Telemetry'}
          </span>
          {isHi ? info.hi : info.en}
        </span>
      )}
    </span>
  );
};

export const JatakaBirthBlueprintModal: React.FC<JatakaBirthBlueprintModalProps> = ({
  isOpen,
  onClose,
  kundali,
  lang: initialLang = 'hi',
  chartStyle: initialChartStyle = 'north'
}) => {
  const [selectedLang, setSelectedLang] = useState<Language>(initialLang || 'hi');
  const [selectedChartStyle, setSelectedChartStyle] = useState<ChartStyle>(initialChartStyle || 'north');
  // Default to Side-by-Side (SPLIT) per user instruction
  const [activeMainTab, setActiveMainTab] = useState<'SPLIT' | 'PROFILE' | 'CHARTS'>('SPLIT');
  const [selectedTriChart, setSelectedTriChart] = useState<'all' | 'D1' | 'Chandra' | 'D9'>('all');
  const [copied, setCopied] = useState(false);

  if (!isOpen || !kundali) return null;

  const isHi = selectedLang === 'hi' || selectedLang === 'sa';
  const { birthDetails, planets = [], houses = [], charts = {} } = kundali;
  const rawCharts = charts as Record<string, any>;

  const moonPlanet = planets.find((p) => p.name === 'Moon');
  const ascPlanet = planets.find((p) => (p.name as string) === 'Ascendant' || (p.name as string) === 'Lagna');

  // Moon house from lagna for accurate classical Paya
  const moonHouseNum = (moonPlanet as any)?.house || 1;

  const ascendant = ascPlanet ? {
    sign: ascPlanet.sign,
    degreeFormatted: ascPlanet.degreeFormatted,
    nakshatra: ascPlanet.nakshatra,
    pada: ascPlanet.pada
  } : {
    sign: houses?.[0]?.sign || 'Taurus',
    degreeFormatted: '07°39\'08"',
    nakshatra: 'Krittika',
    pada: 4
  };

  // Precision Classical Calculations
  const ishta = calculateIshtaKaal(
    birthDetails?.date || '2013-01-01',
    birthDetails?.time || '08:40:00',
    birthDetails?.latitude || 28.9,
    birthDetails?.longitude || 76.63,
    moonPlanet,
    ascPlanet
  );

  const avakahada = getAvakahadaDetails(
    moonPlanet?.nakshatra || 'Uttara Phalguni',
    moonPlanet?.pada || 1,
    moonHouseNum
  );

  const ghatChakra = getGhatChakraDetails(moonPlanet?.sign || 'Leo');

  // Pure Classical Chart rendering helper
  const renderKundaliChart = (chartData: any, customSize?: string) => {
    if (!chartData) return null;
    const chartPlanets = chartData.planets || planets || [];
    const sizeClasses = customSize || 'w-full aspect-square max-w-[240px] sm:max-w-[260px] mx-auto';

    if (selectedChartStyle === 'south') {
      return (
        <div className={sizeClasses}>
          <SouthIndianSvgChart chartData={chartData} planets={chartPlanets} lang={selectedLang} />
        </div>
      );
    }
    if (selectedChartStyle === 'east') {
      return (
        <div className={sizeClasses}>
          <EastIndianSvgChart chartData={chartData} planets={chartPlanets} lang={selectedLang} />
        </div>
      );
    }
    return (
      <div className={sizeClasses}>
        <NorthIndianSvgChart chartData={chartData} planets={chartPlanets} lang={selectedLang} />
      </div>
    );
  };

  // One-Click "Copy Bhrigu Summary"
  const handleCopySummary = () => {
    const summaryText = `==============================
📜 जातक जन्म पत्रिका विवरण (Bhrigu Patrika Blueprint)
==============================
नाम: ${birthDetails?.name || 'Native'}
जन्म तिथि: ${birthDetails?.date || ''} • समय: ${birthDetails?.time || ''}
जन्म स्थान: ${birthDetails?.placeName || 'Unknown'} (अक्षांश: ${birthDetails?.latitude?.toFixed(4) || ''}°, देशांतर: ${birthDetails?.longitude?.toFixed(4) || ''}°)

--- १. खगोलीय काल माप व पंचांग (Ganita & Time) ---
इष्टकाल: ${ishta.ishtaKaalFormatted}
दिनमान: ${ishta.dinmaanGhati} | रात्रिमान: ${ishta.ratrimaanGhati}
स्थानिक संस्कार (LMT): ${ishta.sthanikSanskar} | स्थानिक समय: ${ishta.sthanikSamaya}
वेलांतर: ${ishta.velantar} | साम्पातिक काल: ${ishta.samaptikKaal}
सूर्योदय: ${ishta.sunriseFormatted} | सूर्यास्त: ${ishta.sunsetFormatted}
संवत्: विक्रम ${ishta.samvatVikram} / शक ${ishta.samvatSaka} | ऋतु: ${ishta.ritu}
जन्म तिथि: ${ishta.birthTithi} | जन्म नक्षत्र: ${ishta.birthNakshatra}
भयात: ${ishta.bhayatFormatted} | भभोग: ${ishta.bhabhogFormatted} (अनुपात: ${ishta.bhayatBhabhogProgress}%)
जन्मकालीन भोग्य दशा शेष: ${ishta.birthBhogyaDasha}

--- २. अवकहड़ा एवं घात चक्र (Avakahada & Ghat) ---
लग्न: ${ascendant.sign} | चन्द्र राशि: ${moonPlanet?.sign || ''}
नक्षत्र-चरण: ${moonPlanet?.nakshatra || ''} - चरण ${moonPlanet?.pada || 1}
वर्ण: ${avakahada.varna} | वश्य: ${avakahada.vashya} | योनि: ${avakahada.taraYoni}
गण: ${avakahada.gana} | नाड़ी: ${avakahada.nadi} | हंसक: ${avakahada.hansak}
पाया: ${avakahada.paya} (${avakahada.payaEffect})
नामाक्षर: ${avakahada.namakshar}
घात मास: ${ghatChakra.ghatMaas} | घात तिथि: ${ghatChakra.ghatTithi} | घात वार: ${ghatChakra.ghatVaar} | घात नक्षत्र: ${ghatChakra.ghatNakshatra}

==============================`;

    navigator.clipboard.writeText(summaryText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // ---------------------------------------------------------------------------
  // SUB-RENDERERS FOR PILLARS & CHARTS (SHARED ACROSS VIEWS)
  // ---------------------------------------------------------------------------

  // Pillar 1: Bhrigu Ganita & Time Telemetry
  const renderPillar1 = () => (
    <div className="space-y-3.5">
      <div className="border-b-2 border-[#174C4B] pb-1 flex items-center justify-between">
        <h3 className="font-serif font-bold text-sm text-[#174C4B] uppercase tracking-wide flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-[#B58A4A]" />
          <span>{isHi ? '१. खगोलीय काल माप व पंचांग' : '1. Bhrigu Ganita & Time'}</span>
        </h3>
        <span className="text-[10px] font-mono font-bold bg-[#E8F0EF] text-[#174C4B] px-1.5 py-0.2 rounded border border-[#174C4B]/20">
          {ishta.samvatVikram} संवत्
        </span>
      </div>

      {/* 1. Precision Time Telemetry Card */}
      <div className="bg-[#FFFDF7] p-3 rounded-2xl border border-[#E5DDD0] shadow-2xs space-y-2">
        <div className="text-[11px] font-bold text-[#174C4B] border-b border-[#E5DDD0] pb-1 uppercase tracking-wider flex items-center justify-between">
          <span>{isHi ? 'इष्टकाल एवं स्थानिक संस्कार' : 'Solar Time Telemetry'}</span>
          <span className="text-[9px] font-mono text-[#B58A4A]">{ishta.ayana}</span>
        </div>

        <div className="grid grid-cols-2 gap-y-1.5 text-xs">
          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="ishtaKaal" lang={selectedLang}>
              {isHi ? 'इष्टकाल (Ishta Kaal):' : 'Ishta Kaal:'}
            </ClassicalTooltip>
          </span>
          <span className="font-bold font-mono text-[#174C4B] text-[13px]">{ishta.ishtaKaalFormatted}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="dinmaan" lang={selectedLang}>
              {isHi ? 'दिनमान (Day Duration):' : 'Dinmaan:'}
            </ClassicalTooltip>
          </span>
          <span className="font-mono">{ishta.dinmaanGhati}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="ratrimaan" lang={selectedLang}>
              {isHi ? 'रात्रिमान (Night Duration):' : 'Ratrimaan:'}
            </ClassicalTooltip>
          </span>
          <span className="font-mono">{ishta.ratrimaanGhati}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="sthanikSanskar" lang={selectedLang}>
              {isHi ? 'स्थानिक संस्कार (LMT):' : 'LMT Offset:'}
            </ClassicalTooltip>
          </span>
          <span className="font-mono">{ishta.sthanikSanskar}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="sthanikSamaya" lang={selectedLang}>
              {isHi ? 'स्थानिक समय (LMT Time):' : 'Local Mean Time:'}
            </ClassicalTooltip>
          </span>
          <span className="font-mono font-bold text-[#174C4B]">{ishta.sthanikSamaya}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="velantar" lang={selectedLang}>
              {isHi ? 'वेलांतर (Eq. of Time):' : 'Eq. of Time:'}
            </ClassicalTooltip>
          </span>
          <span className="font-mono">{ishta.velantar}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="samaptikKaal" lang={selectedLang}>
              {isHi ? 'साम्पातिक काल (Sidereal):' : 'Sidereal Time:'}
            </ClassicalTooltip>
          </span>
          <span className="font-mono font-semibold text-[#174C4B]">{ishta.samaptikKaal}</span>

          <span className="text-[#4B5956]">{isHi ? 'सूर्योदय / सूर्यास्त:' : 'Sunrise / Sunset:'}</span>
          <span className="font-mono text-[11px]">{ishta.sunriseFormatted} / {ishta.sunsetFormatted}</span>
        </div>
      </div>

      {/* 2. Classical Panchang Telemetry Card */}
      <div className="bg-[#FFFDF7] p-3 rounded-2xl border border-[#E5DDD0] shadow-2xs space-y-2">
        <div className="text-[11px] font-bold text-[#174C4B] border-b border-[#E5DDD0] pb-1 uppercase tracking-wider flex items-center justify-between">
          <span>{isHi ? 'पंचांग एवं संवत्सर' : 'Classical Panchang'}</span>
          <span className="text-[9px] font-mono text-[#7B8782]">{ishta.samvatSaka} शक</span>
        </div>

        <div className="grid grid-cols-2 gap-y-1.5 text-xs">
          <span className="text-[#4B5956]">{isHi ? 'संवत् (विक्रम / शक):' : 'Samvat (Vikram / Saka):'}</span>
          <span className="font-medium font-mono">{ishta.samvatVikram} / {ishta.samvatSaka}</span>

          <span className="text-[#4B5956]">{isHi ? 'ऋतु एवं अयन:' : 'Season & Solstice:'}</span>
          <span>{ishta.ritu} • {ishta.ayana}</span>

          <span className="text-[#4B5956]">{isHi ? 'जन्म तिथि:' : 'Birth Tithi:'}</span>
          <span className="font-bold text-[#174C4B]">{ishta.birthTithi}</span>

          <span className="text-[#4B5956]">{isHi ? 'जन्म नक्षत्र:' : 'Birth Constellation:'}</span>
          <span className="font-bold text-[#174C4B]">{ishta.birthNakshatra}</span>
        </div>
      </div>

      {/* 3. Bhayat, Bhabhog & Bhogya Dasha Balance Card */}
      <div className="bg-[#FFFDF7] p-3 rounded-2xl border border-[#E5DDD0] shadow-2xs space-y-2">
        <div className="text-[11px] font-bold text-[#174C4B] border-b border-[#E5DDD0] pb-1 uppercase tracking-wider flex items-center justify-between">
          <span>{isHi ? 'भयात, भभोग व दशा भुक्ति' : 'Bhayat & Balance'}</span>
          <span className="text-[9px] font-mono font-bold text-[#B58A4A]">{ishta.bhayatBhabhogProgress}%</span>
        </div>

        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between items-center">
            <span className="text-[#4B5956]">
              <ClassicalTooltip termKey="bhayat" lang={selectedLang}>
                {isHi ? 'भयात (Bhayat):' : 'Bhayat:'}
              </ClassicalTooltip>
            </span>
            <span className="font-mono font-bold">{ishta.bhayatFormatted}</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-[#4B5956]">
              <ClassicalTooltip termKey="bhabhog" lang={selectedLang}>
                {isHi ? 'भभोग (Bhabhog):' : 'Bhabhog:'}
              </ClassicalTooltip>
            </span>
            <span className="font-mono font-bold">{ishta.bhabhogFormatted}</span>
          </div>

          {/* Graphical Ghati Traverse Bar */}
          <div className="w-full bg-[#E5DDD0] h-2 rounded-full overflow-hidden">
            <div
              className="bg-gradient-to-r from-[#174C4B] to-[#D4AF37] h-full transition-all duration-300"
              style={{ width: `${Math.min(100, Math.max(5, ishta.bhayatBhabhogProgress))}%` }}
              title={`Bhayat/Bhabhog Moon Constellation Traversal: ${ishta.bhayatBhabhogProgress}%`}
            />
          </div>

          <div className="pt-1.5 border-t border-[#E5DDD0] flex justify-between items-center">
            <span className="text-[#4B5956]">
              <ClassicalTooltip termKey="bhogyaDasha" lang={selectedLang}>
                {isHi ? 'जन्मकालीन भोग्य दशा शेष:' : 'Birth Dasha Balance:'}
              </ClassicalTooltip>
            </span>
            <span className="font-bold text-[#174C4B] font-serif text-[12px]">{ishta.birthBhogyaDasha}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Pillar 2: Avakahada & Ghat Chakra
  const renderPillar2 = () => (
    <div className="space-y-3.5">
      <div className="border-b-2 border-[#174C4B] pb-1 flex items-center justify-between">
        <h3 className="font-serif font-bold text-sm text-[#174C4B] uppercase tracking-wide flex items-center gap-1.5">
          <Award className="w-4 h-4 text-[#B58A4A]" />
          <span>{isHi ? '२. अवकहड़ा एवं घात चक्र' : '2. Avakahada & Ghat Chakra'}</span>
        </h3>
        <span className="text-[10px] font-bold px-2 py-0.2 rounded-full bg-[#D4AF37]/20 text-[#174C4B] border border-[#D4AF37]/40">
          {avakahada.paya} पाया
        </span>
      </div>

      {/* 1. Avakahada Harmonic Grid Card */}
      <div className="bg-[#FFFDF7] p-3 rounded-2xl border border-[#E5DDD0] shadow-2xs space-y-2">
        <div className="text-[11px] font-bold text-[#174C4B] border-b border-[#E5DDD0] pb-1 uppercase tracking-wider flex items-center justify-between">
          <span>{isHi ? 'अवकहड़ा चक्र (Natal Coordinates)' : 'Avakahada Harmonic Factors'}</span>
          <span className="text-[10px] font-bold text-[#B58A4A]">{avakahada.hansak}</span>
        </div>

        <div className="grid grid-cols-2 gap-y-1.5 text-xs">
          <span className="text-[#4B5956]">{isHi ? 'लग्न / लग्न स्वामी:' : 'Lagna & Lord:'}</span>
          <span className="font-bold">{getZodiacName(ascendant.sign, selectedLang)} ({avakahada.rashiLord})</span>

          <span className="text-[#4B5956]">{isHi ? 'राशि / राशि स्वामी:' : 'Moon Sign & Lord:'}</span>
          <span className="font-bold">{getZodiacName(moonPlanet?.sign || 'Leo', selectedLang)} ({avakahada.rashiLord})</span>

          <span className="text-[#4B5956]">{isHi ? 'नक्षत्र - चरण:' : 'Star & Quarter:'}</span>
          <span className="font-bold">{getNakshatraName(moonPlanet?.nakshatra || 'Uttara Phalguni', selectedLang)} - {moonPlanet?.pada || 1}</span>

          <span className="text-[#4B5956]">{isHi ? 'नक्षत्र स्वामी:' : 'Star Lord:'}</span>
          <span className="font-semibold">{avakahada.nakshatraLord}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="varna" lang={selectedLang}>
              {isHi ? 'वर्ण (Varna):' : 'Varna:'}
            </ClassicalTooltip>
          </span>
          <span>{avakahada.varna}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="vashya" lang={selectedLang}>
              {isHi ? 'वश्य (Vashya):' : 'Vashya:'}
            </ClassicalTooltip>
          </span>
          <span>{avakahada.vashya}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="yoni" lang={selectedLang}>
              {isHi ? 'योनि (Yoni):' : 'Yoni:'}
            </ClassicalTooltip>
          </span>
          <span>{avakahada.taraYoni}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="gana" lang={selectedLang}>
              {isHi ? 'गण (Gana):' : 'Gana:'}
            </ClassicalTooltip>
          </span>
          <span>{avakahada.gana}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="nadi" lang={selectedLang}>
              {isHi ? 'नाड़ी (Nadi):' : 'Nadi:'}
            </ClassicalTooltip>
          </span>
          <span>{avakahada.nadi}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="hansak" lang={selectedLang}>
              {isHi ? 'हंसक (Tattva):' : 'Hansak (Tattva):'}
            </ClassicalTooltip>
          </span>
          <span>{avakahada.hansak}</span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="paya" lang={selectedLang}>
              {isHi ? 'पाया (Paya):' : 'Paya:'}
            </ClassicalTooltip>
          </span>
          <span className="font-semibold text-[#174C4B]" title={avakahada.payaEffect}>
            {avakahada.paya}
          </span>

          <span className="text-[#4B5956]">
            <ClassicalTooltip termKey="namakshar" lang={selectedLang}>
              {isHi ? 'जन्म नामाक्षर:' : 'Auspicious Initial:'}
            </ClassicalTooltip>
          </span>
          <span className="font-bold text-[#174C4B] text-[13px]">{avakahada.namakshar}</span>
        </div>
      </div>

      {/* 2. Ghat Chakra Sensitivity Table */}
      <div className="bg-[#FFFDF7] p-3 rounded-2xl border border-[#E5DDD0] shadow-2xs space-y-2">
        <div className="flex items-center justify-between border-b border-[#E5DDD0] pb-1">
          <span className="text-[11px] font-bold text-[#174C4B] uppercase tracking-wider flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5 text-[#B58A4A]" />
            <ClassicalTooltip termKey="ghatChakra" lang={selectedLang}>
              <span>{isHi ? 'घात चक्र (Sensitivity Coordinates)' : 'Ghat Chakra Grid'}</span>
            </ClassicalTooltip>
          </span>
          <span className="text-[9px] text-[#7B8782] italic">
            {isHi ? 'परिहार्य योग' : 'Avoidance Matrix'}
          </span>
        </div>

        {/* Primary Avoidance Factors */}
        <div className="grid grid-cols-2 gap-2 text-center text-xs">
          <div className="p-1.5 rounded-xl bg-[#F7F3EA] border border-[#E5DDD0]">
            <span className="text-[10px] text-[#7B8782] block">{isHi ? 'घात मास' : 'Ghat Month'}</span>
            <span className="font-bold text-[#174C4B]">{ghatChakra.ghatMaas}</span>
          </div>
          <div className="p-1.5 rounded-xl bg-[#F7F3EA] border border-[#E5DDD0]">
            <span className="text-[10px] text-[#7B8782] block">{isHi ? 'घात तिथि' : 'Ghat Tithi'}</span>
            <span className="font-bold text-[#174C4B]">{ghatChakra.ghatTithi}</span>
          </div>
          <div className="p-1.5 rounded-xl bg-[#F7F3EA] border border-[#E5DDD0]">
            <span className="text-[10px] text-[#7B8782] block">{isHi ? 'घात वार' : 'Ghat Day'}</span>
            <span className="font-bold text-[#174C4B]">{ghatChakra.ghatVaar}</span>
          </div>
          <div className="p-1.5 rounded-xl bg-[#F7F3EA] border border-[#E5DDD0]">
            <span className="text-[10px] text-[#7B8782] block">{isHi ? 'घात नक्षत्र' : 'Ghat Star'}</span>
            <span className="font-bold text-[#174C4B]">{ghatChakra.ghatNakshatra}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-1.5 text-center text-[10.5px] pt-1">
          <div className="p-1 rounded-lg bg-[#FAF6ED] border border-[#E5DDD0]">
            <span className="text-[9px] text-[#7B8782] block">प्रहर</span>
            <span className="font-semibold">{ghatChakra.ghatPrahar}</span>
          </div>
          <div className="p-1 rounded-lg bg-[#FAF6ED] border border-[#E5DDD0]">
            <span className="text-[9px] text-[#7B8782] block">वर्ग</span>
            <span className="font-semibold">{ghatChakra.ghatVarga}</span>
          </div>
          <div className="p-1 rounded-lg bg-[#FAF6ED] border border-[#E5DDD0]">
            <span className="text-[9px] text-[#7B8782] block">लग्न</span>
            <span className="font-semibold">{ghatChakra.ghatLagna}</span>
          </div>
        </div>

        {/* Sensitive Planetary Signs */}
        <div className="pt-2 border-t border-[#E5DDD0] space-y-1">
          <span className="text-[10px] font-bold text-[#7B8782] block uppercase tracking-wider">
            {isHi ? 'घात ग्रह राशियां (Sensitive Signs):' : 'Sensitive Planetary Signs:'}
          </span>
          <div className="grid grid-cols-4 gap-1 text-[10px] text-center">
            <span className="p-1 rounded bg-[#F2EDE2]">सूर्य: <b>{ghatChakra.ghatSunSign}</b></span>
            <span className="p-1 rounded bg-[#F2EDE2]">चन्द्र: <b>{ghatChakra.ghatMoonSign}</b></span>
            <span className="p-1 rounded bg-[#F2EDE2]">मंगल: <b>{ghatChakra.ghatMarsSign}</b></span>
            <span className="p-1 rounded bg-[#F2EDE2]">बुध: <b>{ghatChakra.ghatMercurySign}</b></span>
            <span className="p-1 rounded bg-[#F2EDE2]">गुरु: <b>{ghatChakra.ghatJupiterSign}</b></span>
            <span className="p-1 rounded bg-[#F2EDE2]">शुक्र: <b>{ghatChakra.ghatVenusSign}</b></span>
            <span className="p-1 rounded bg-[#F2EDE2]">शनि: <b>{ghatChakra.ghatSaturnSign}</b></span>
            <span className="p-1 rounded bg-[#F2EDE2]">राहु: <b>{ghatChakra.ghatRahuSign}</b></span>
          </div>
        </div>
      </div>
    </div>
  );

  // Pillar 3: Master Ephemeris Table
  const renderPillar3 = () => (
    <div className="space-y-3.5">
      <div className="border-b-2 border-[#174C4B] pb-1 flex items-center justify-between">
        <h3 className="font-serif font-bold text-sm text-[#174C4B] uppercase tracking-wide flex items-center gap-1.5">
          <Compass className="w-4 h-4 text-[#B58A4A]" />
          <span>{isHi ? '३. ग्रह स्पष्ट तथा स्थिति' : '3. Precise Ephemeris Table'}</span>
        </h3>
        <span className="text-[10px] font-mono text-[#7B8782]">
          लाहिड़ी (चित्रापक्ष अयनांश)
        </span>
      </div>

      {/* Planetary Longitudes Table */}
      <div className="bg-[#FFFDF7] rounded-2xl border border-[#E5DDD0] shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-[#F2EDE2] text-[#174C4B] border-b border-[#E5DDD0] text-[10.5px]">
                <th className="p-2 font-bold">{isHi ? 'ग्रह' : 'Planet'}</th>
                <th className="p-2 font-bold">{isHi ? 'राशि' : 'Rashi'}</th>
                <th className="p-2 font-bold">{isHi ? 'स्पष्टांश' : 'Degrees'}</th>
                <th className="p-2 font-bold">{isHi ? 'नक्षत्र (पाद)' : 'Nakshatra (Pada)'}</th>
                <th className="p-2 font-bold">{isHi ? 'अवस्था' : 'Dignity'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5DDD0]">
              {/* Ascendant Row */}
              <tr className="bg-[#FAF6ED] font-semibold text-[#174C4B]">
                <td className="p-2 flex items-center gap-1 font-serif">
                  <span className="text-[11px]">✨</span>
                  <span>{isHi ? 'लग्न (Asc)' : 'Lagna'}</span>
                </td>
                <td className="p-2">{getZodiacName(ascendant.sign, selectedLang)}</td>
                <td className="p-2 font-mono font-bold text-[#174C4B]">{ascendant.degreeFormatted}</td>
                <td className="p-2">{getNakshatraName(ascendant.nakshatra, selectedLang)} - {ascendant.pada}</td>
                <td className="p-2 text-[10px] text-[#7B8782]">{isHi ? 'लग्न भाव' : 'Ascendant'}</td>
              </tr>

              {/* Nine Grahas */}
              {planets.map((p) => (
                <tr key={p.name} className="hover:bg-[#FAF6ED] transition-colors">
                  <td className="p-2 font-medium flex items-center gap-1">
                    <span className="font-serif">{getPlanetFullName(p.name, selectedLang)}</span>
                    {p.isRetrograde && (
                      <span className="text-[9.5px] font-bold text-amber-700 bg-amber-100 px-1 rounded" title="वक्री (Retrograde)">
                        व
                      </span>
                    )}
                    {p.isCombust && (
                      <span className="text-[9.5px] font-bold text-rose-700 bg-rose-100 px-1 rounded" title="अस्त (Combust)">
                        अ
                      </span>
                    )}
                  </td>
                  <td className="p-2">{getZodiacName(p.sign, selectedLang)}</td>
                  <td className="p-2 font-mono font-bold text-[#174C4B]">{p.degreeFormatted}</td>
                  <td className="p-2">
                    {getNakshatraName(p.nakshatra, selectedLang).slice(0, 7)} - {p.pada}
                  </td>
                  <td className="p-2 font-medium text-[10px] text-[#4B5956] truncate max-w-[85px]">
                    {p.dignity || 'मित्र राशि'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Astronomical Symbol Legend */}
        <div className="px-3 py-2 bg-[#F7F3EA] border-t border-[#E5DDD0] text-[9.5px] text-[#4B5956] font-serif text-center flex items-center justify-around flex-wrap gap-1">
          <span><b>व</b> = वक्री (Retrograde)</span>
          <span><b>अ</b> = अस्त (Combust)</span>
          <span><b>पू</b> = पूर्ण अस्त (Cazimi)</span>
          <span><b>स</b> = स्थिर (Direct)</span>
          <span><b>राहु</b> = स्पष्ट (True Node)</span>
        </div>
      </div>

      {/* Classical Note */}
      <div className="p-3 bg-[#FAF6ED] rounded-2xl border border-[#E5DDD0] text-[11px] text-[#4B5956] leading-relaxed">
        <span className="font-bold text-[#174C4B] block mb-0.5">
          {isHi ? 'भृगु पत्रिका खगोलीय नियम:' : 'Classical Astronomical Standard:'}
        </span>
        {isHi
          ? 'सभी ग्रहों की स्थिति निरयण पद्धति एवं चित्रापक्ष अयनांश के आधार पर सूर्य सिद्धांत व आधुनिक खगोल गणित के सामंजस्य से शुद्ध की गई है।'
          : 'All planetary longitudes are computed on the Nirayana sidereal zodiac using the Chitrapaksha (Lahiri) Ayanamsha calibrated with high-precision ephemeris.'}
      </div>
    </div>
  );

  // Tri-Kundali Gallery (D1, Moon, D9)
  const renderTriKundaliGallery = (layout: 'vertical' | 'horizontal') => (
    <div className="space-y-4">
      <div className="bg-[#FFFDF7] p-3 sm:p-4 rounded-3xl border border-[#E5DDD0] shadow-xs space-y-3">
        <div className="border-b border-[#E5DDD0] pb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-serif font-bold text-base text-[#174C4B] flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#B58A4A]" />
            <span>{isHi ? 'त्रिकुण्डली दर्शन (लग्न, चन्द्र एवं नवमांश)' : 'Tri-Kundali Master Stage (D1, Moon & D9)'}</span>
          </h3>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5 bg-[#FAF6ED] p-0.5 rounded-lg border border-[#E5DDD0] text-[11px]">
              <button
                type="button"
                onClick={() => setSelectedTriChart('all')}
                className={`px-2 py-0.5 rounded-md font-semibold transition cursor-pointer ${
                  selectedTriChart === 'all' ? 'bg-[#174C4B] text-white shadow-2xs' : 'text-[#4B5956] hover:text-[#174C4B]'
                }`}
              >
                {isHi ? 'तीनों' : 'All 3'}
              </button>
              <button
                type="button"
                onClick={() => setSelectedTriChart('D1')}
                className={`px-2 py-0.5 rounded-md font-semibold transition cursor-pointer ${
                  selectedTriChart === 'D1' ? 'bg-[#174C4B] text-white shadow-2xs' : 'text-[#4B5956] hover:text-[#174C4B]'
                }`}
              >
                {isHi ? 'लग्न D1' : 'D1'}
              </button>
              <button
                type="button"
                onClick={() => setSelectedTriChart('Chandra')}
                className={`px-2 py-0.5 rounded-md font-semibold transition cursor-pointer ${
                  selectedTriChart === 'Chandra' ? 'bg-[#174C4B] text-white shadow-2xs' : 'text-[#4B5956] hover:text-[#174C4B]'
                }`}
              >
                {isHi ? 'चन्द्र' : 'Moon'}
              </button>
              <button
                type="button"
                onClick={() => setSelectedTriChart('D9')}
                className={`px-2 py-0.5 rounded-md font-semibold transition cursor-pointer ${
                  selectedTriChart === 'D9' ? 'bg-[#174C4B] text-white shadow-2xs' : 'text-[#4B5956] hover:text-[#174C4B]'
                }`}
              >
                {isHi ? 'नवमांश D9' : 'D9'}
              </button>
            </div>
            <span className="text-[10px] font-mono text-[#7B8782]">
              {selectedChartStyle.toUpperCase()}
            </span>
          </div>
        </div>

        <div className={layout === 'horizontal' ? 'grid grid-cols-1 md:grid-cols-3 gap-4' : 'space-y-4'}>
          {/* D1 Lagna */}
          {(selectedTriChart === 'all' || selectedTriChart === 'D1') && (
            <div className="bg-[#FAF6ED] p-3 rounded-2xl border border-[#E5DDD0] text-center space-y-2">
              <div className="flex items-center justify-between px-1">
                <span className="font-bold text-sm text-[#174C4B] font-serif">
                  {isHi ? 'लग्न-चलित कुण्डली (D1)' : 'Lagna-Chalit (D1)'}
                </span>
                <span className="text-[10.5px] font-mono font-bold bg-[#E8F0EF] text-[#174C4B] px-1.5 py-0.2 rounded">
                  {ascendant.degreeFormatted}
                </span>
              </div>
              <div className="p-1 bg-white border border-[#E5DDD0] rounded-xl shadow-2xs">
                {renderKundaliChart(rawCharts.D1 || kundali, selectedTriChart === 'D1' ? 'w-full aspect-square max-w-[340px] mx-auto' : 'w-full aspect-square max-w-[260px] mx-auto')}
              </div>
            </div>
          )}

          {/* Chandra Kundali */}
          {(selectedTriChart === 'all' || selectedTriChart === 'Chandra') && (
            <div className="bg-[#FAF6ED] p-3 rounded-2xl border border-[#E5DDD0] text-center space-y-2">
              <div className="flex items-center justify-between px-1">
                <span className="font-bold text-sm text-[#174C4B] font-serif">
                  {isHi ? 'चन्द्र कुण्डली (Moon)' : 'Chandra Kundali (Moon)'}
                </span>
                <span className="text-[10.5px] font-mono font-bold bg-[#E8F0EF] text-[#174C4B] px-1.5 py-0.2 rounded">
                  {moonPlanet?.sign || 'Leo'}
                </span>
              </div>
              <div className="p-1 bg-white border border-[#E5DDD0] rounded-xl shadow-2xs">
                {renderKundaliChart(rawCharts.Chandra || rawCharts.D1 || kundali, selectedTriChart === 'Chandra' ? 'w-full aspect-square max-w-[340px] mx-auto' : 'w-full aspect-square max-w-[260px] mx-auto')}
              </div>
            </div>
          )}

          {/* D9 Navamsha */}
          {(selectedTriChart === 'all' || selectedTriChart === 'D9') && (
            <div className="bg-[#FAF6ED] p-3 rounded-2xl border border-[#E5DDD0] text-center space-y-2">
              <div className="flex items-center justify-between px-1">
                <span className="font-bold text-sm text-[#174C4B] font-serif">
                  {isHi ? 'नवमांश कुण्डली (D9)' : 'Navamsha Kundali (D9)'}
                </span>
                <span className="text-[10.5px] font-mono font-bold bg-[#E8F0EF] text-[#174C4B] px-1.5 py-0.2 rounded">
                  Harmonic
                </span>
              </div>
              <div className="p-1 bg-white border border-[#E5DDD0] rounded-xl shadow-2xs">
                {renderKundaliChart(rawCharts.D9 || rawCharts.D1 || kundali, selectedTriChart === 'D9' ? 'w-full aspect-square max-w-[340px] mx-auto' : 'w-full aspect-square max-w-[260px] mx-auto')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const modalNode = (
    <div
      id="jataka-birth-blueprint-overlay"
      className="fixed inset-0 z-[9999] bg-[#0B2625]/85 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 overflow-y-auto animate-fadeIn select-text"
      onClick={onClose}
    >
      <div
        id="jataka-birth-blueprint-dialog"
        className="bg-[#FFFDF7] text-[#263332] border-2 border-[#174C4B] rounded-3xl w-full max-w-7xl max-h-[96vh] flex flex-col shadow-2xl overflow-hidden animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================================================================= */}
        {/* TOP HEADER: CIVIC IDENTITY & MASTER CONTROLS                      */}
        {/* ================================================================= */}
        <div className="px-4 sm:px-5 py-3 bg-[#0E2E2D] text-[#FAF6ED] border-b border-[#08201F] flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <span className="p-1.5 rounded-xl bg-[#D4AF37] text-[#082120] shadow-sm">
              <Compass className="w-4 h-4 text-[#082120]" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base sm:text-lg font-bold font-serif tracking-wide text-[#FCD34D] drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                  {birthDetails?.name || (isHi ? 'जातक विवरण' : 'Jataka Birth Profile')}
                </h2>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0A2423] text-[#FCD34D] border border-[#1C4D4A] uppercase tracking-wider">
                  {isHi ? 'भृगु पत्रिका स्वरूप' : 'Bhrigu Patrika Blueprint'}
                </span>
              </div>
              <p className="text-[11px] text-[#A6BCB4] font-mono leading-none mt-1">
                {formatDOB(birthDetails?.date)} • {birthDetails?.time || '08:40:00'} • {birthDetails?.placeName || 'Rohtak, India'}
              </p>
            </div>
          </div>

          {/* Controls: Primary View Tabs, Chart Style, Copy Summary, Language & Close */}
          <div className="flex items-center flex-wrap gap-2">
            {/* View Mode Segmented Controls - DEFAULT TO SIDE-BY-SIDE */}
            <div className="flex items-center gap-0.5 bg-[#071F1E] p-0.5 rounded-xl border border-white/15 text-xs text-white">
              <button
                type="button"
                onClick={() => setActiveMainTab('SPLIT')}
                className={`px-3 py-1 rounded-lg font-bold transition flex items-center gap-1.5 cursor-pointer text-xs ${
                  activeMainTab === 'SPLIT' ? 'bg-[#D4AF37] text-[#082120] shadow-xs' : 'text-[#E0EAE6] hover:text-white'
                }`}
                title="Side-by-side Jataka Profile & Tri-Kundali"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                <span>{isHi ? 'संयुक्त दर्शन (Side-by-Side)' : 'Side-by-Side'}</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveMainTab('PROFILE')}
                className={`px-3 py-1 rounded-lg font-bold transition flex items-center gap-1.5 cursor-pointer text-xs ${
                  activeMainTab === 'PROFILE' ? 'bg-[#D4AF37] text-[#082120] shadow-xs' : 'text-[#E0EAE6] hover:text-white'
                }`}
                title="View Pure Jataka Profile Dossier (Panchang, Avakahada, Ghat Chakra & Ephemeris)"
              >
                <FileText className="w-3.5 h-3.5" />
                <span>{isHi ? 'विवरण स्तम्भ' : 'Profile Dossier'}</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveMainTab('CHARTS')}
                className={`px-3 py-1 rounded-lg font-bold transition flex items-center gap-1.5 cursor-pointer text-xs ${
                  activeMainTab === 'CHARTS' ? 'bg-[#D4AF37] text-[#082120] shadow-xs' : 'text-[#E0EAE6] hover:text-white'
                }`}
                title="View Three Kundalis Full-Width (Lagna D1, Moon, Navamsha D9)"
              >
                <Layers className="w-3.5 h-3.5" />
                <span>{isHi ? 'त्रिकुण्डली' : 'Tri-Kundali'}</span>
              </button>
            </div>

            {/* Regional Chart Style Selector */}
            <div className="flex items-center gap-0.5 bg-[#071F1E] p-0.5 rounded-xl border border-white/15 text-xs text-white">
              <button
                type="button"
                onClick={() => setSelectedChartStyle('north')}
                className={`px-1.5 py-1 rounded-lg font-bold transition cursor-pointer text-[11px] ${
                  selectedChartStyle === 'north' ? 'bg-[#D4AF37] text-[#082120]' : 'text-white/80 hover:text-white'
                }`}
              >
                {isHi ? 'उत्तर' : 'North'}
              </button>
              <button
                type="button"
                onClick={() => setSelectedChartStyle('south')}
                className={`px-1.5 py-1 rounded-lg font-bold transition cursor-pointer text-[11px] ${
                  selectedChartStyle === 'south' ? 'bg-[#D4AF37] text-[#082120]' : 'text-white/80 hover:text-white'
                }`}
              >
                {isHi ? 'दक्षिण' : 'South'}
              </button>
              <button
                type="button"
                onClick={() => setSelectedChartStyle('east')}
                className={`px-1.5 py-1 rounded-lg font-bold transition cursor-pointer text-[11px] ${
                  selectedChartStyle === 'east' ? 'bg-[#D4AF37] text-[#082120]' : 'text-white/80 hover:text-white'
                }`}
              >
                {isHi ? 'पूर्व' : 'East'}
              </button>
            </div>

            {/* Language Selector */}
            <div className="flex items-center gap-1 bg-[#071F1E] px-2 py-1 rounded-xl border border-white/15 text-xs text-white">
              <Globe className="w-3.5 h-3.5 text-[#EAD8A4]" />
              <select
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value as Language)}
                className="bg-transparent font-bold text-white focus:outline-none cursor-pointer text-xs"
              >
                <option value="hi" className="text-black">हिन्दी</option>
                <option value="en" className="text-black">English</option>
                <option value="sa" className="text-black">संस्कृतम्</option>
              </select>
            </div>

            {/* One-Click Copy Bhrigu Summary Button */}
            <button
              type="button"
              onClick={handleCopySummary}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#D4AF37] hover:bg-[#E5C158] text-[#082120] font-bold text-xs transition cursor-pointer shadow-sm"
              title="Copy all vital telemetry to clipboard"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-950 stroke-[3]" /> : <Copy className="w-3.5 h-3.5 stroke-[2.5]" />}
              <span>{copied ? (isHi ? 'कॉपी हुआ!' : 'Copied!') : (isHi ? 'प्रतिलिपि' : 'Copy Summary')}</span>
            </button>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-xl bg-[#071F1E] hover:bg-black/55 text-white/90 hover:text-white transition cursor-pointer border border-white/15"
              title="Close Blueprint"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ================================================================= */}
        {/* MODAL BODY                                                        */}
        {/* ================================================================= */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-5 bg-[#FDFBF7]">

          {/* ----------------------------------------------------------------- */}
          {/* VIEW 1: SIDE-BY-SIDE (PRIMARY HERO VIEW — KUNDALIS & DOSSIER)     */}
          {/* ----------------------------------------------------------------- */}
          {activeMainTab === 'SPLIT' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 items-start">
              {/* Left Column (md:col-span-1 lg:col-span-5): Tri-Kundali Stage */}
              <div className="md:col-span-1 lg:col-span-5 xl:col-span-5 space-y-4">
                {renderTriKundaliGallery('vertical')}
              </div>

              {/* Right Column (md:col-span-1 lg:col-span-7): Full Jataka Profile Telemetry Dossier */}
              <div className="md:col-span-1 lg:col-span-7 xl:col-span-7 space-y-4">
                {renderPillar1()}
                {renderPillar2()}
                {renderPillar3()}
              </div>
            </div>
          )}

          {/* ----------------------------------------------------------------- */}
          {/* VIEW 2: PROFILE_DOSSIER_ONLY (FULL-WIDTH 3-COLUMN TELEMETRY)      */}
          {/* ----------------------------------------------------------------- */}
          {activeMainTab === 'PROFILE' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {renderPillar1()}
              {renderPillar2()}
              {renderPillar3()}
            </div>
          )}

          {/* ----------------------------------------------------------------- */}
          {/* VIEW 3: CHARTS_ONLY (FULL-WIDTH 3-COLUMN TRI-KUNDALI GALLERY)     */}
          {/* ----------------------------------------------------------------- */}
          {activeMainTab === 'CHARTS' && (
            <div className="space-y-4">
              {renderTriKundaliGallery('horizontal')}
            </div>
          )}
        </div>

        {/* ================================================================= */}
        {/* BOTTOM ACTION BAR                                                 */}
        {/* ================================================================= */}
        <div className="px-5 py-2.5 bg-[#F2EDE2] border-t border-[#E5DDD0] flex items-center justify-between text-xs shrink-0">
          <span className="text-[11px] text-[#4B5956] italic">
            {isHi ? 'वैदिक ज्योतिष • शुद्ध खगोलीय जन्म कालीन गणित (जातक विवरण एवं त्रिकुण्डली)' : 'Classical Vedic Astronomical Birth Telemetry (Jataka Profile & Tri-Kundali)'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopySummary}
              className="px-3 py-1.5 rounded-xl bg-[#FAF6ED] hover:bg-[#EDE6D8] text-[#174C4B] font-bold transition border border-[#D4AF37]/50 cursor-pointer shadow-2xs flex items-center gap-1"
            >
              {copied ? <Check className="w-3 h-3 text-emerald-800" /> : <Copy className="w-3 h-3" />}
              <span>{copied ? (isHi ? 'कॉपी हुआ!' : 'Copied!') : (isHi ? 'प्रतिलिपि' : 'Copy Summary')}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 rounded-xl bg-[#174C4B] hover:bg-[#0B2625] text-white font-bold transition shadow-xs cursor-pointer"
            >
              {isHi ? 'सम्पन्न / बन्द करें' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Mount directly to document.body via Portal to permanently prevent being behind any chart view or stacking context
  if (typeof document !== 'undefined') {
    return createPortal(modalNode, document.body);
  }
  return modalNode;
};

export default JatakaBirthBlueprintModal;
